/**
 * VolumeTable Component
 *
 * Displays a table of volume resources with actions (view, edit, delete, grant access).
 * Follows the same approach as GroupTable for UI consistency.
 */

import React from 'react';
import { CircleStackIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import type { VolumeSanitized } from '../../../types/volume.types';
import { formatRelativeTime } from '../../../lib/utils/format';

interface VolumeTableProps {
  volumes: VolumeSanitized[];
  isLoading?: boolean;
  onView: (volume: VolumeSanitized) => void;
  onEdit?: (volume: VolumeSanitized) => void;
  onDelete?: (volume: VolumeSanitized) => void;
  /** Permission to edit volumes */
  canEdit?: boolean;
  /** Permission to delete volumes */
  canDelete?: boolean;
}

/**
 * VolumeTable component
 */
export const VolumeTable: React.FC<VolumeTableProps> = ({
  volumes,
  isLoading,
  onView,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={5} />;
  }

  if (volumes.length === 0) {
    return (
      <EmptyState
        icon={<CircleStackIcon className="mx-auto h-12 w-12" />}
        title="No volumes found"
        description="Get started by creating a new volume."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Volume Name
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Code
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Description
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Created
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {volumes.map((item) => (
            <tr key={item.volumeId} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <CircleStackIcon className="h-5 w-5 text-primary-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {item.volumeName}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <CopyableCode value={item.volumeCode} />
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {item.description || (
                    <span className="text-gray-400 italic">No description</span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatRelativeTime(item.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onView(item)}
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Button>
                  {canEdit && onEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onEdit(item)}
                      title="Edit Volume"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && onDelete && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onDelete(item)}
                      title="Delete Volume"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
